import { SelectGroup, SelectItem, SelectLabel } from "@/components/ui/select";
import type React from "react";

export type CategoryForSelect = {
  id: string | number;
  name: string | null;
  scope?: string | null;
  color?: string | null;
};

interface CategorySelectGroupsProps<T extends CategoryForSelect = CategoryForSelect> {
  categories: T[];
  /** Returns the SelectItem value string. Default: cat.name! */
  getValue?: (cat: T) => string;
  /** Custom content rendered inside each SelectItem. Default: cat.name */
  renderContent?: (cat: T) => React.ReactNode;
  /** Optional data-testid per item */
  getTestId?: (cat: T) => string;
}

/**
 * Renders category options grouped into "Custom" (scope≠goals) and "Goal Categories" (scope=goals).
 * Group headers only appear when BOTH types are present; otherwise renders a flat list with no headers.
 * Place this inside a <SelectContent>.
 */
export function CategorySelectGroups<T extends CategoryForSelect = CategoryForSelect>({
  categories,
  getValue = (cat) => cat.name!,
  renderContent,
  getTestId,
}: CategorySelectGroupsProps<T>) {
  const valid = categories.filter((c) => c.name);
  const custom = valid.filter((c) => c.scope !== "goals");
  const goals = valid.filter((c) => c.scope === "goals");

  const renderItem = (cat: T) => (
    <SelectItem
      key={cat.id}
      value={getValue(cat)}
      {...(getTestId ? { "data-testid": getTestId(cat) } : {})}
    >
      {renderContent ? renderContent(cat) : cat.name}
    </SelectItem>
  );

  // Only add group headers when both types co-exist
  if (custom.length === 0 || goals.length === 0) {
    return <>{valid.map(renderItem)}</>;
  }

  return (
    <>
      <SelectGroup>
        <SelectLabel>Custom</SelectLabel>
        {custom.map(renderItem)}
      </SelectGroup>
      <SelectGroup>
        <SelectLabel>Goal Categories</SelectLabel>
        {goals.map(renderItem)}
      </SelectGroup>
    </>
  );
}
